import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { pricing as localPricing, stateMultiplier, TVA_TAUX, MARGE_COORDINATION } from '../data/pricing';
import { prixSelonEtat } from '../utils/releveDepuisDossier';
import PageSEO from '../components/PageSEO';

// ─── Room types ───────────────────────────────────────────────────────────────
const ROOM_TYPES = [
  { id: 'sejour',  label: 'Séjour / Salon',       icon: '🛋️', surfaceMin: 10, surfaceMax: 60,  surfaceDefault: 25 },
  { id: 'cuisine', label: 'Cuisine',               icon: '🍳', surfaceMin: 5,  surfaceMax: 25,  surfaceDefault: 12 },
  { id: 'chambre', label: 'Chambre',               icon: '🛏️', surfaceMin: 8,  surfaceMax: 25,  surfaceDefault: 14 },
  { id: 'sdb',     label: 'Salle de bain',         icon: '🚿', surfaceMin: 3,  surfaceMax: 14,  surfaceDefault: 7  },
  { id: 'wc',      label: 'WC séparé',             icon: '🚽', surfaceMin: 1,  surfaceMax: 5,   surfaceDefault: 2  },
  // { id: 'bureau',  label: 'Bureau',                icon: '💼', surfaceMin: 8,  surfaceMax: 40,  surfaceDefault: 15 },
  { id: 'couloir', label: 'Couloir / Entrée',      icon: '🚪', surfaceMin: 2,  surfaceMax: 20,  surfaceDefault: 6  },
  { id: 'facade',  label: 'Façade / Extérieur',     icon: '🏗️', surfaceMin: 20, surfaceMax: 400, surfaceDefault: 80 },
  { id: 'autre',   label: 'Autre pièce',           icon: '📦', surfaceMin: 5,  surfaceMax: 60,  surfaceDefault: 20 },
];

// ─── Étages ───────────────────────────────────────────────────────────────────
// Un logement se décrit par niveau : un client au téléphone (22/09/2026) ne
// pouvait pas dire que sa maison en avait deux — toutes les pièces tombaient
// dans le même sac. Tant qu'il n'y a qu'un niveau, l'interface n'en parle pas :
// un plain-pied ne doit pas payer la complexité d'une maison à étages.
const FLOOR_NAMES = ['Rez-de-chaussée', '1er étage', '2e étage', '3e étage', '4e étage', '5e étage'];
const floorNameFor = (index) => FLOOR_NAMES[index] || `${index}e étage`;

// ─── Item groups (collapsible sections per room) ──────────────────────────────
const ITEM_GROUPS = [
  { id: 'sols',        label: 'Sols & revêtements',         icon: '🏠' },
  { id: 'murs',        label: 'Murs & plafonds',           icon: '🖌️' },
  { id: 'plomberie',   label: 'Plomberie & sanitaires',    icon: '🚰' },
  { id: 'electricite', label: 'Électricité & éclairage',   icon: '⚡' },
  { id: 'menuiseries', label: 'Menuiseries & rangements',  icon: '🚪' },
  { id: 'chauffage',   label: 'Chauffage & climatisation', icon: '🌡️' },
  { id: 'demolition',  label: 'Démolition & gros œuvre',   icon: '🔨' },
  { id: 'divers',      label: 'Divers & chantier',         icon: '📦' },
  { id: 'ite',         label: 'Isolation thermique extérieure', icon: '🧱' },
  { id: 'facade',      label: 'Façade & ravalement',       icon: '🎨' },
];

const DEFAULT_OPEN_GROUPS = {
  sejour:  ['sols', 'murs'],
  cuisine: ['sols', 'murs', 'plomberie'],
  chambre: ['sols', 'murs'],
  sdb:     ['plomberie', 'sols', 'murs'],
  wc:      ['plomberie', 'murs'],
  bureau:  ['sols', 'murs', 'electricite'],
  couloir: ['sols', 'murs'],
  facade:  ['facade', 'ite'],
  autre:   ['sols', 'murs'],
};

// ─── Items per room ───────────────────────────────────────────────────────────
// perSurface: true   → checkbox; area = surface * surfaceMult (auto-calc)
// sliderItem: true   → slider; value in ml or m²
// perSurface: false  → +/− counter
// Group is derived from key prefix (e.g. 'sols.xxx' → group 'sols')
const ROOM_ITEMS = {
  sejour: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.ragreage',                perSurface: true,  label: 'Ragréage sol' },
    { key: 'sols.parquet_stratifie',       perSurface: true,  label: 'Parquet stratifié' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage grès cérame' },
    { key: 'sols.sol_pvc',                 perSurface: true,  label: 'Sol PVC / vinyle' },
    { key: 'sols.plinthes',                perSurface: true,  label: 'Plinthes (ml)' },
    // Murs
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 2.5, label: 'Peinture murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    { key: 'murs.enduit_rebouchage',       perSurface: true,  surfaceMult: 2.5, label: 'Enduit rebouchage' },
    { key: 'murs.cloison_placo_std',       sliderItem: true,  sliderMax: 20, mlToM2: true, label: 'Cloison Placo' },
    { key: 'murs.cloison_placo_acou',      sliderItem: true,  sliderMax: 20, mlToM2: true, label: 'Cloison acoustique' },
    { key: 'demolition.demolition_cloison_np',          sliderItem: true,  sliderMax: 15, mlToM2: true, label: 'Dépose cloison' },
    { key: 'murs.plafond_suspendu',        perSurface: true,  label: 'Faux plafond suspendu' },
    // Électricité
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise de courant' },
    { key: 'electricite.interrupteur',     perSurface: false, label: 'Interrupteur' },
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    { key: 'electricite.circuit_electrique', perSurface: false, label: 'Circuit neuf' },
    { key: 'electricite.radiateur_electrique', perSurface: false, label: 'Radiateur électrique' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    { key: 'menuiseries.porte_coulissante',perSurface: false, label: 'Porte coulissante galandage' },
    { key: 'menuiseries.fenetre_pvc',      perSurface: false, label: 'Fenêtre PVC' },
    { key: 'menuiseries.placard_sur_mesure', perSurface: false, label: 'Placard sur mesure' },
    // Chauffage
    { key: 'chauffage.clim_mono_split',    perSurface: false, label: 'Climatisation split' },
    { key: 'chauffage.radiateur_eau',      perSurface: false, label: 'Radiateur eau chaude' },
    { key: 'chauffage.plancher_chauffant', perSurface: true,  label: 'Plancher chauffant' },
    // Démolition
    { key: 'demolition.demolition_cloison_np', sliderItem: true, sliderMax: 15, mlToM2: true, label: 'Démolition cloison' },
    { key: 'demolition.ouverture_porteuse',    perSurface: false, label: 'Ouverture mur porteur' },
    { key: 'demolition.percement_mur',         perSurface: false, label: 'Percement mur' },
  ],
  cuisine: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage sol' },
    { key: 'sols.sol_pvc',                 perSurface: true,  label: 'Sol PVC / vinyle' },
    { key: 'sols.plinthes',                perSurface: true,  label: 'Plinthes (ml)' },
    // Murs
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 2.5, label: 'Peinture murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    { key: 'murs.carrelage_faience_sdb',   sliderItem: true,  sliderMax: 10, label: 'Crédence carrelage (m²)' },
    { key: 'murs.plafond_suspendu',        perSurface: true,  label: 'Faux plafond suspendu' },
    // Plomberie
    { key: 'plomberie.evier_cuisine',      perSurface: false, label: 'Évier cuisine' },
    { key: 'plomberie.robinetterie_lavabo',perSurface: false, label: 'Robinetterie évier' },
    { key: 'plomberie.chauffe_eau_elec',   perSurface: false, label: 'Chauffe-eau électrique 200L' },
    { key: 'plomberie.chauffe_eau_thermo', perSurface: false, label: 'Chauffe-eau thermodynamique' },
    { key: 'plomberie.creation_point_eau', perSurface: false, label: 'Création point d\'eau' },
    { key: 'plomberie.reseau_sdb',         perSurface: false, label: 'Réseau plomberie neuf' },
    { key: 'plomberie.evacuation_sdb',     perSurface: false, label: 'Réseau évacuation PVC' },
    // Électricité
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise de courant' },
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    { key: 'electricite.circuit_electrique', perSurface: false, label: 'Circuit neuf' },
    { key: 'electricite.vmc_simple_flux',  perSurface: false, label: 'VMC simple flux' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    // Chauffage
    { key: 'chauffage.clim_mono_split',    perSurface: false, label: 'Climatisation split' },
    { key: 'chauffage.radiateur_eau',      perSurface: false, label: 'Radiateur eau chaude' },
    // Démolition
    { key: 'demolition.depose_sanitaires', perSurface: false, label: 'Dépose sanitaires existants' },
    { key: 'demolition.percement_mur',     perSurface: false, label: 'Percement mur' },
  ],
  chambre: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.parquet_stratifie',       perSurface: true,  label: 'Parquet stratifié' },
    { key: 'sols.sol_pvc',                 perSurface: true,  label: 'Sol PVC / vinyle' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage' },
    { key: 'sols.plinthes',                perSurface: true,  label: 'Plinthes (ml)' },
    // Murs
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 2.5, label: 'Peinture murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    { key: 'murs.enduit_rebouchage',       perSurface: true,  surfaceMult: 2.5, label: 'Enduit rebouchage' },
    { key: 'murs.cloison_placo_std',       sliderItem: true,  sliderMax: 15, mlToM2: true, label: 'Cloison Placo' },
    // Électricité
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise de courant' },
    { key: 'electricite.interrupteur',     perSurface: false, label: 'Interrupteur' },
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    { key: 'electricite.radiateur_electrique', perSurface: false, label: 'Radiateur électrique' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    { key: 'menuiseries.placard_sur_mesure', perSurface: false, label: 'Placard sur mesure' },
    { key: 'menuiseries.fenetre_pvc',      perSurface: false, label: 'Fenêtre PVC' },
    // Chauffage
    { key: 'chauffage.clim_mono_split',    perSurface: false, label: 'Climatisation split' },
    { key: 'chauffage.radiateur_eau',      perSurface: false, label: 'Radiateur eau chaude' },
    { key: 'chauffage.plancher_chauffant', perSurface: true,  label: 'Plancher chauffant' },
  ],
  sdb: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage sol' },
    { key: 'sols.carrelage_douche_sol',    perSurface: false, label: 'Carrelage sol douche (antidérapant)' },
    { key: 'sols.carrelage_douche_mural',  perSurface: false, label: 'Carrelage mural douche' },
    { key: 'sols.mosaique_sdb',            perSurface: false, label: 'Mosaïque salle de bain' },
    { key: 'sols.membrane_etancheite',     perSurface: true,  label: 'Membrane étanchéité zone humide' },
    { key: 'sols.bande_deformable',        sliderItem: true,  sliderMax: 20, label: 'Bande déformable étanchéité (ml)' },
    // Murs
    { key: 'murs.carrelage_faience_sdb',   perSurface: true,  surfaceMult: 2.5, label: 'Faïence murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    // Plomberie
    { key: 'plomberie.douche_italienne',   perSurface: false, label: 'Douche à l\'italienne' },
    { key: 'plomberie.baignoire',          perSurface: false, label: 'Baignoire' },
    { key: 'plomberie.wc_standard',        perSurface: false, label: 'WC suspendu' },
    { key: 'plomberie.wc_pmr',            perSurface: false, label: 'WC PMR' },
    { key: 'plomberie.lavabo',             perSurface: false, label: 'Lavabo / vasque' },
    { key: 'plomberie.lave_mains',         perSurface: false, label: 'Lave-mains' },
    { key: 'plomberie.robinetterie_lavabo',perSurface: false, label: 'Robinetterie lavabo' },
    { key: 'plomberie.robinetterie_bain',  perSurface: false, label: 'Robinetterie douche/bain' },
    { key: 'plomberie.chauffe_eau_elec',   perSurface: false, label: 'Chauffe-eau électrique 200L' },
    { key: 'plomberie.chauffe_eau_thermo', perSurface: false, label: 'Chauffe-eau thermodynamique' },
    { key: 'plomberie.groupe_securite',    perSurface: false, label: 'Groupe de sécurité' },
    { key: 'plomberie.reseau_sdb',         perSurface: false, label: 'Réseau plomberie neuf' },
    { key: 'plomberie.evacuation_sdb',     perSurface: false, label: 'Réseau évacuation PVC' },
    // Électricité
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise sèche-serviette' },
    { key: 'electricite.vmc_simple_flux',  perSurface: false, label: 'VMC simple flux' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    // Démolition
    { key: 'demolition.depose_sanitaires', perSurface: false, label: 'Dépose sanitaires existants' },
  ],
  wc: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage sol' },
    // Murs
    { key: 'murs.carrelage_faience_sdb',   perSurface: true,  surfaceMult: 3.0, label: 'Faïence murs' },
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 3.0, label: 'Peinture murs' },
    // Plomberie
    { key: 'plomberie.wc_standard',        perSurface: false, label: 'WC suspendu' },
    { key: 'plomberie.wc_pmr',            perSurface: false, label: 'WC PMR' },
    { key: 'plomberie.lave_mains',         perSurface: false, label: 'Lave-mains' },
    // Électricité
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.vmc_simple_flux',  perSurface: false, label: 'VMC / extracteur' },
  ],
  bureau: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.parquet_stratifie',       perSurface: true,  label: 'Parquet stratifié' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage' },
    { key: 'sols.sol_pvc',                 perSurface: true,  label: 'Sol PVC / vinyle' },
    { key: 'sols.plinthes',                perSurface: true,  label: 'Plinthes (ml)' },
    // Murs
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 2.5, label: 'Peinture murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    { key: 'murs.plafond_suspendu',        perSurface: true,  label: 'Faux plafond suspendu' },
    { key: 'murs.cloison_placo_acou',      sliderItem: true,  sliderMax: 20, mlToM2: true, label: 'Cloison acoustique' },
    { key: 'murs.cloison_placo_std',       sliderItem: true,  sliderMax: 20, mlToM2: true, label: 'Cloison Placo' },
    // Électricité
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise de courant' },
    { key: 'electricite.prise_rj45',       perSurface: false, label: 'Prise réseau RJ45' },
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    { key: 'electricite.panneau_led_600',  perSurface: false, label: 'Panneau LED 60×60' },
    { key: 'electricite.circuit_electrique', perSurface: false, label: 'Circuit neuf' },
    { key: 'electricite.radiateur_electrique', perSurface: false, label: 'Radiateur électrique' },
    { key: 'electricite.alarme_incendie',  perSurface: false, label: 'Alarme incendie TYPE 3' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    { key: 'menuiseries.fenetre_pvc',      perSurface: false, label: 'Fenêtre PVC' },
    { key: 'menuiseries.placard_sur_mesure', perSurface: false, label: 'Placard sur mesure' },
    { key: 'menuiseries.banque_accueil',   perSurface: false, label: 'Banque d\'accueil' },
    // Chauffage
    { key: 'chauffage.clim_mono_split',    perSurface: false, label: 'Climatisation split' },
    { key: 'chauffage.clim_cassette',      perSurface: false, label: 'Cassette de climatisation' },
  ],
  couloir: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage' },
    { key: 'sols.parquet_stratifie',       perSurface: true,  label: 'Parquet stratifié' },
    { key: 'sols.plinthes',                perSurface: true,  label: 'Plinthes (ml)' },
    // Murs
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 3.5, label: 'Peinture murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    // Électricité
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise de courant' },
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    { key: 'menuiseries.placard_sur_mesure', perSurface: false, label: 'Placard / meuble entrée' },
  ],
  facade: [
    // Façade
    { key: 'facade.nettoyage_haute_pression', perSurface: true,  label: 'Nettoyage haute pression' },
    { key: 'facade.peinture_facade',          perSurface: true,  label: 'Peinture façade' },
    { key: 'facade.lasure_bois',              perSurface: true,  label: 'Lasure bois façade' },
    { key: 'facade.enduit_projete',           perSurface: true,  label: 'Enduit projeté taloché' },
    { key: 'facade.enduit_gratte',            perSurface: true,  label: 'Enduit gratté / tyrolienne' },
    { key: 'facade.rebouchage_fissures',      sliderItem: true,  sliderMax: 30, label: 'Rebouchage fissures (ml)' },
    { key: 'facade.peinture_volets_bois',     perSurface: false, label: 'Peinture volets bois' },
    // ITE
    { key: 'ite.ite_laine_roche',             perSurface: true,  label: 'Isolation ext. laine de roche' },
    { key: 'ite.ite_bardage',                 perSurface: true,  label: 'Isolation ext. bardage bois/composite' },
    { key: 'ite.ite_depose_ravalement',       perSurface: true,  label: 'Dépose ancien revêtement façade' },
    { key: 'ite.ite_echafaudage',             perSurface: true,  label: 'Échafaudage façade' },
    { key: 'ite.ite_habillage_baie',          sliderItem: true,  sliderMax: 40, label: 'Habillage retours de baies (ml)' },
    { key: 'ite.ite_profil_depart',           sliderItem: true,  sliderMax: 40, label: 'Profilé de départ (ml)' },
    // Isolation
    { key: 'isolation.isolation_murs_int',     perSurface: true,  label: 'Isolation murs ITI' },
    { key: 'isolation.isolation_combles',      perSurface: true,  label: 'Isolation combles perdus' },
    { key: 'isolation.isolation_plancher',     perSurface: true,  label: 'Isolation plancher bas' },
    // Divers
    { key: 'divers.echafaudage_semaine',       perSurface: false, label: 'Location échafaudage (semaine)' },
    { key: 'demolition.evacuation_gravats',    perSurface: false, label: 'Évacuation gravats en benne' },
    { key: 'divers.nettoyage_fin_chantier',    perSurface: false, label: 'Nettoyage fin de chantier' },
  ],
  autre: [
    // Sols
    { key: 'sols.depose_ancien_sol',       perSurface: true,  label: 'Dépose ancien revêtement' },
    { key: 'sols.ragreage',                perSurface: true,  label: 'Ragréage sol' },
    { key: 'sols.carrelage_600',           perSurface: true,  label: 'Carrelage' },
    { key: 'sols.parquet_stratifie',       perSurface: true,  label: 'Parquet stratifié' },
    { key: 'sols.sol_pvc',                 perSurface: true,  label: 'Sol PVC / vinyle' },
    { key: 'sols.plinthes',                perSurface: true,  label: 'Plinthes (ml)' },
    // Murs
    { key: 'murs.peinture_murs',           perSurface: true,  surfaceMult: 2.5, label: 'Peinture murs' },
    { key: 'murs.peinture_plafond',        perSurface: true,  label: 'Peinture plafond' },
    { key: 'murs.plafond_suspendu',        perSurface: true,  label: 'Faux plafond suspendu' },
    { key: 'murs.cloison_placo_std',       sliderItem: true,  sliderMax: 20, mlToM2: true, label: 'Cloison Placo' },
    // Plomberie
    { key: 'plomberie.creation_point_eau', perSurface: false, label: 'Création point d\'eau' },
    // Électricité
    { key: 'electricite.prise_simple',     perSurface: false, label: 'Prise de courant' },
    { key: 'electricite.point_lumineux',   perSurface: false, label: 'Point lumineux' },
    { key: 'electricite.spot_encastre',    perSurface: false, label: 'Spot encastré LED' },
    { key: 'electricite.radiateur_electrique', perSurface: false, label: 'Radiateur électrique' },
    { key: 'electricite.tableau_neuf',     perSurface: false, label: 'Tableau électrique neuf' },
    { key: 'electricite.mise_aux_normes_elec', perSurface: false, label: 'Mise aux normes élec.' },
    // Menuiseries
    { key: 'menuiseries.porte_interieure', perSurface: false, label: 'Porte intérieure' },
    // Chauffage
    { key: 'chauffage.clim_mono_split',    perSurface: false, label: 'Climatisation split' },
    { key: 'chauffage.radiateur_eau',      perSurface: false, label: 'Radiateur eau chaude' },
    { key: 'chauffage.chaudiere_gaz_cond', perSurface: false, label: 'Chaudière gaz condensation' },
    { key: 'chauffage.pac_air_eau',        perSurface: false, label: 'PAC air/eau' },
    // Démolition
    { key: 'demolition.demolition_cloison_np', sliderItem: true, sliderMax: 15, mlToM2: true, label: 'Démolition cloison' },
    { key: 'demolition.ouverture_porteuse',    perSurface: false, label: 'Ouverture mur porteur' },
    { key: 'demolition.percement_mur',         perSurface: false, label: 'Percement mur' },
    { key: 'demolition.depose_porte',          perSurface: false, label: 'Dépose porte + huisserie' },
    { key: 'demolition.evacuation_gravats',    perSurface: false, label: 'Évacuation gravats' },
    // Divers
    { key: 'divers.installation_chantier',     perSurface: false, label: 'Installation chantier' },
    { key: 'divers.nettoyage_fin_chantier',    perSurface: false, label: 'Nettoyage fin de chantier' },
    { key: 'demolition.evacuation_gravats',    perSurface: false, label: 'Évacuation gravats en benne' },
    // ITE
    { key: 'ite.ite_bardage',                 perSurface: true,  label: 'Isolation ext. bardage bois/composite' },
    { key: 'ite.ite_laine_roche',             perSurface: true,  label: 'Isolation ext. laine de roche' },
    { key: 'ite.ite_depose_ravalement',       perSurface: true,  label: 'Dépose ancien revêtement façade' },
    { key: 'ite.ite_echafaudage',             perSurface: true,  label: 'Échafaudage façade' },
    { key: 'ite.ite_habillage_baie',          sliderItem: true,  sliderMax: 40, label: 'Habillage retours de baies (ml)' },
    { key: 'ite.ite_profil_depart',           sliderItem: true,  sliderMax: 40, label: 'Profilé de départ (ml)' },
    // Façade
    { key: 'facade.nettoyage_haute_pression', perSurface: true,  label: 'Nettoyage haute pression façade' },
    { key: 'facade.peinture_facade',          perSurface: true,  label: 'Peinture façade' },
    { key: 'facade.lasure_bois',              perSurface: true,  label: 'Lasure bois façade' },
    { key: 'facade.enduit_projete',           perSurface: true,  label: 'Enduit projeté taloché' },
    { key: 'facade.enduit_gratte',            perSurface: true,  label: 'Enduit gratté / tyrolienne' },
    { key: 'facade.rebouchage_fissures',      sliderItem: true,  sliderMax: 30, label: 'Rebouchage fissures (ml)' },
    { key: 'facade.peinture_volets_bois',     perSurface: false, label: 'Peinture volets bois' },
  ],
};

// Default pre-checked items per room type
const DEFAULT_CHECKED = {
  sejour:  ['murs.peinture_murs', 'murs.peinture_plafond'],
  cuisine: ['murs.peinture_murs', 'murs.peinture_plafond'],
  chambre: ['murs.peinture_murs', 'murs.peinture_plafond'],
  sdb:     ['sols.carrelage_600', 'murs.carrelage_faience_sdb'],
  wc:      ['sols.carrelage_600', 'murs.carrelage_faience_sdb', 'plomberie.wc_standard'],
  bureau:  ['murs.peinture_murs', 'murs.peinture_plafond'],
  couloir: ['murs.peinture_murs', 'murs.peinture_plafond'],
  facade:  [],
  autre:   ['murs.peinture_murs', 'murs.peinture_plafond'],
};


// Keys that trigger forced peinture_murs
const PLACO_TRIGGER_KEYS = ['murs.cloison_placo_std', 'demolition.demolition_cloison_np', 'murs.cloison_placo_acou'];

function resolveItem(pricing, key) {
  const [cat, id] = key.split('.');
  return pricing[cat]?.[id] || null;
}

function formatEur(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

// isSurfaceWork: true → prix nuancé selon l'état du bien (pos), d'au plus 5 % sous le maxi
// isSurfaceWork: false → équipements, à mi-amplitude
function effectivePrice(priceData, pos, isSurfaceWork = true) {
  return prixSelonEtat(priceData, isSurfaceWork ? pos : 0.5);
}

function priceUnit(p, pos, isSurfaceWork = true) {
  return `${formatEur(Math.round(effectivePrice(p, pos, isSurfaceWork)))} / ${p.unit}`;
}

const STEPS = ['Type de bien', 'Pièces & travaux', 'Vos coordonnées', 'Votre estimation'];

// ─── Surface item → checkbox with auto-inferred area ─────────────────────────
function SurfaceItem({ pricing, itemConfig, surface, checked, onToggle, pos, forced }) {
  const priceData = resolveItem(pricing, itemConfig.key);
  if (!priceData) return null;
  const area = Math.round(surface * (itemConfig.surfaceMult || 1));
  const areaLabel = area > 0 ? `~${area} m²` : '—';
  const ep = effectivePrice(priceData, pos);
  const estimate = area > 0 ? formatEur(Math.round(area * ep)) : '—';

  return (
    <div
      onClick={() => surface > 0 && !forced && onToggle()}
      className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
        surface === 0 ? 'opacity-40 cursor-not-allowed border-slate-200 bg-white' :
        forced ? 'border-orange-400 bg-orange-50 cursor-not-allowed' :
        checked ? 'border-orange-400 bg-orange-50 cursor-pointer' : 'border-slate-200 bg-white hover:border-orange-300 cursor-pointer'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${checked || forced ? 'border-orange-500 bg-orange-500' : 'border-slate-300'}`}>
          {(checked || forced) && <span className="text-white text-xs leading-none">✓</span>}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">{itemConfig.label}</p>
          <p className="text-xs text-slate-400">{priceUnit(priceData, pos)} · {areaLabel}</p>
          {forced && <p className="text-xs text-orange-500 font-medium">Requis (travaux placo)</p>}
        </div>
      </div>
      {(checked || forced) && area > 0 && (
        <span className="text-sm font-bold text-orange-600 ml-4 shrink-0">{estimate}</span>
      )}
    </div>
  );
}

const WALL_HEIGHT = 2.5;

// ─── Slider item ──────────────────────────────────────────────────────────────
function SliderItem({ pricing, itemConfig, value, onChange, pos }) {
  const priceData = resolveItem(pricing, itemConfig.key);
  if (!priceData) return null;
  const max = itemConfig.sliderMax || 20;
  const m2 = itemConfig.mlToM2 ? value * WALL_HEIGHT : value;
  const ep = effectivePrice(priceData, pos);
  const lineTotal = m2 * ep;
  const active = value > 0;
  const sliderUnit = itemConfig.mlToM2 ? 'ml' : priceData.unit;

  const unitLabel = itemConfig.mlToM2
    ? `${formatEur(Math.round(ep))} / m² · haut. ${WALL_HEIGHT} m`
    : priceUnit(priceData, pos);

  return (
    <div className={`px-4 py-3 rounded-xl border transition-all ${active ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-slate-700">{itemConfig.label}</p>
          <p className="text-xs text-slate-400">{unitLabel}</p>
        </div>
        <div className="text-right shrink-0">
          <span className={`text-sm font-bold ${active ? 'text-orange-600' : 'text-slate-400'}`}>
            {value} {sliderUnit}
          </span>
          {itemConfig.mlToM2 && active && (
            <p className="text-xs text-slate-400">≈ {m2.toFixed(1)} m²</p>
          )}
        </div>
      </div>
      <input type="range" min="0" max={max} step="1" value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-orange-500" />
      <div className="flex justify-between text-xs text-slate-300 mt-0.5">
        <span>0 {sliderUnit}</span>
        {active && <span className="text-orange-600 font-semibold">{formatEur(Math.round(lineTotal))}</span>}
        <span>{max} {sliderUnit}</span>
      </div>
    </div>
  );
}

// ─── Unit item → +/− counter ─────────────────────────────────────────────────
function UnitItem({ pricing, itemConfig, qty, onUpdate, pos }) {
  const priceData = resolveItem(pricing, itemConfig.key);
  if (!priceData) return null;
  const ep = effectivePrice(priceData, pos, false);

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${qty > 0 ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700">{itemConfig.label}</p>
        <p className="text-xs text-slate-400">{priceUnit(priceData, pos, false)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => onUpdate(Math.max(0, qty - 1))}
          className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 hover:bg-slate-100 font-bold text-sm">−</button>
        <span className="w-6 text-center font-semibold text-slate-800 text-sm">{qty}</span>
        <button onClick={() => onUpdate(qty + 1)}
          className="w-7 h-7 rounded-full border border-orange-300 bg-orange-50 flex items-center justify-center text-orange-600 hover:bg-orange-100 font-bold text-sm">+</button>
      </div>
      {qty > 0 && <span className="text-sm font-bold text-orange-600 shrink-0">{formatEur(Math.round(qty * ep))}</span>}
    </div>
  );
}

// ─── Custom free-text item ────────────────────────────────────────────────────
function CustomItem({ text, onChange }) {
  const active = text.trim().length > 0;
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${active ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700">Autre</p>
        <input
          type="text"
          value={text}
          onChange={e => onChange(e.target.value)}
          placeholder="Décrire…"
          className="mt-1 w-full text-xs border border-slate-200 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-orange-400 bg-white"
        />
        {active && <p className="text-xs text-orange-500 mt-1">À chiffrer après visite</p>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Devis() {
  const [pricing, setPricing] = useState(localPricing);
  useEffect(() => {
    fetch('/api/pricing')
      .then(r => r.json())
      .then(data => { if (data) setPricing(data); })
      .catch(() => {}); // keep local pricing on error
  }, []);

  const stepSectionRef = React.useRef(null);
  const goToStep = (n) => {
    setStep(n);
    setTimeout(() => {
      if (stepSectionRef.current) {
        const top = stepSectionRef.current.getBoundingClientRect().top + window.scrollY - 112;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 0);
  };
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const [propertyType, setPropertyType] = useState('maison');
  const [totalSurface, setTotalSurface] = useState(80);
  const [state, setState] = useState('bon_etat');
  const [tvaKey, setTvaKey] = useState('tva_10');
  const [floors, setFloors] = useState([{ id: 1, name: FLOOR_NAMES[0] }]);
  const [rooms, setRooms] = useState([]);
  const [expandedRoomId, setExpandedRoomId] = useState(null);
  const multiFloor = floors.length > 1;
  const floorNameOf = (floorId) => floors.find(f => f.id === floorId)?.name || '';

  const [contact, setContact] = useState({ prenom: '', nom: '', email: '', telephone: '', adresse: '', ville: '' });
  const updateContact = (k, v) => setContact(p => ({ ...p, [k]: v }));

  // Pré-remplissage depuis le hero form de Home (location.state.prefill).
  // Valable une seule fois à l'entrée sur /devis — le user peut ensuite
  // modifier librement propertyType, surface, coordonnées.
  const location = useLocation();
  useEffect(() => {
    const p = location.state?.prefill;
    if (!p) return;
    const validTypes = new Set(['maison', 'appartement', 'local_commercial', 'bureau_pro']);
    const validStates = new Set(['neuf', 'bon_etat', 'a_renover', 'mauvais']);
    const validTvas = new Set(['tva_55', 'tva_10', 'tva_20']);
    const hasType  = p.propertyType && validTypes.has(p.propertyType);
    const surfaceNum = parseInt(p.surface, 10);
    const hasSurface = Number.isFinite(surfaceNum) && surfaceNum > 0;
    const hasState = p.etat && validStates.has(p.etat);
    const hasTva   = p.tvaKey && validTvas.has(p.tvaKey);

    if (hasType)    setPropertyType(p.propertyType);
    if (hasSurface) setTotalSurface(surfaceNum);
    if (hasState)   setState(p.etat);
    if (hasTva)     setTvaKey(p.tvaKey);
    if (p.codePostal) setContact(c => ({ ...c, ville: c.ville || p.codePostal }));

    // Si tout est rempli (hero form complet), skip direct à l'étape
    // "Pièces & travaux" — user arrive déjà sur le configurateur.
    if (hasType && hasSurface && hasState && hasTva) {
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRoom = (type, floorId = floors[0]?.id) => {
    const defaults = DEFAULT_CHECKED[type] || [];
    const quantities = {};
    defaults.forEach(k => { quantities[k] = 1; });
    const rt = ROOM_TYPES.find(r => r.id === type);
    const newId = Date.now();
    setRooms(r => [...r, { id: newId, floorId, type, surface: rt?.surfaceDefault || 15, quantities, customText: '' }]);
    setExpandedRoomId(newId);
    setTimeout(() => {
      const el = document.getElementById(`room-${newId}`);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 112;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };
  const addFloor = () => {
    const newId = Date.now();
    setFloors(f => [...f, { id: newId, name: floorNameFor(f.length) }]);
    setTimeout(() => {
      const el = document.getElementById(`floor-${newId}`);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 112, behavior: 'smooth' });
    }, 50);
  };
  const renameFloor = (id, name) => setFloors(f => f.map(fl => fl.id === id ? { ...fl, name } : fl));
  const removeFloor = (id) => {
    // Supprimer un niveau emporte ses pièces : on le dit avant, pas après.
    const attached = rooms.filter(r => r.floorId === id).length;
    if (attached > 0 && !window.confirm(
      `Supprimer ce niveau retirera aussi ${attached} pièce${attached > 1 ? 's' : ''} et leurs travaux. Continuer ?`)) return;
    setFloors(f => {
      const rest = f.filter(fl => fl.id !== id);
      // Jamais zéro niveau : le configurateur n'aurait plus où poser une pièce.
      return rest.length ? rest : [{ id: Date.now(), name: FLOOR_NAMES[0] }];
    });
    setRooms(r => r.filter(room => room.floorId !== id));
  };

  const removeRoom = (id) => {
    setRooms(r => r.filter(room => room.id !== id));
    setExpandedRoomId(prev => prev === id ? null : prev);
  };
  const updateRoomSurface = (id, val) => setRooms(r => r.map(room => room.id === id ? { ...room, surface: val } : room));
  const toggleSurfaceItem = (id, key) => setRooms(r => r.map(room => {
    if (room.id !== id) return room;
    if (key === 'murs.peinture_murs') {
      const placoActive = PLACO_TRIGGER_KEYS.some(k => (room.quantities[k] || 0) > 0);
      if (placoActive) return room; // locked — can't uncheck while placo is active
    }
    const cur = room.quantities[key] || 0;
    return { ...room, quantities: { ...room.quantities, [key]: cur > 0 ? 0 : 1 } };
  }));
  const updateSliderQty = (id, key, qty) => setRooms(r => r.map(room => {
    if (room.id !== id) return room;
    const newQtys = { ...room.quantities, [key]: qty };
    if (PLACO_TRIGGER_KEYS.includes(key) && qty > 0) {
      newQtys['murs.peinture_murs'] = 1;
    }
    return { ...room, quantities: newQtys };
  }));
  const updateUnitQty = (id, key, qty) => setRooms(r => r.map(room =>
    room.id === id ? { ...room, quantities: { ...room.quantities, [key]: qty } } : room
  ));
  const updateCustomText = (id, text) => setRooms(r => r.map(room =>
    room.id === id ? { ...room, customText: text } : room
  ));

  const estimate = useMemo(() => {
    const pos = stateMultiplier[state]?.pos ?? 0.33;
    const tvaRate = TVA_TAUX[tvaKey]?.rate || 0.10;
    let lines = [];

    rooms.forEach(room => {
      const surf = room.surface || 0;
      const roomType = ROOM_TYPES.find(rt => rt.id === room.type);
      const items = ROOM_ITEMS[room.type] || [];
      // « 1er étage · Chambre » : sur un plain-pied le préfixe n'apporte rien.
      const roomLabel = floors.length > 1
        ? `${floors.find(f => f.id === room.floorId)?.name || ''} · ${roomType?.label || room.type}`
        : (roomType?.label || room.type);

      items.forEach(itemConfig => {
        const qty = room.quantities[itemConfig.key] || 0;
        if (qty === 0) return;
        const priceData = resolveItem(pricing, itemConfig.key);
        if (!priceData) return;

        const isSurfaceWork = !!(itemConfig.perSurface || itemConfig.sliderItem);
        const ep = effectivePrice(priceData, pos, isSurfaceWork);
        let lineTotal, qtyLabel;
        if (itemConfig.perSurface) {
          const area = surf * (itemConfig.surfaceMult || 1);
          lineTotal = area * ep;
          qtyLabel = `~${Math.round(area)} m²`;
        } else if (itemConfig.sliderItem && itemConfig.mlToM2) {
          const area = qty * WALL_HEIGHT;
          lineTotal = area * ep;
          qtyLabel = `${qty} ml → ${area.toFixed(1)} m²`;
        } else {
          lineTotal = qty * ep;
          qtyLabel = `${qty} ${priceData.unit}`;
        }

        lines.push({
          roomLabel,
          label: itemConfig.label,
          qty: qtyLabel,
          total: lineTotal,
        });
      });

      if (room.customText?.trim()) {
        lines.push({
          roomLabel,
          label: `Autre : ${room.customText.trim()}`,
          qty: '—',
          total: 0,
          custom: true,
        });
      }
    });

    const ht = lines.reduce((s, l) => s + l.total, 0);
    const coordination = ht * MARGE_COORDINATION;
    const htTotal = ht + coordination;
    const tva = htTotal * tvaRate;
    return { lines, ht, coordination, htTotal, tvaRate, tva, ttc: htTotal + tva };
  }, [rooms, floors, state, tvaKey, pricing]);   // floors : renommer un niveau réétiquette les lignes ; pricing : le barème arrive après le montage

  const handleSendEmail = async () => {
    setSending(true);
    setSendError('');
    try {
      // Build structured data for the email
      const pos = stateMultiplier[state]?.pos ?? 0.33;
      const roomsForEmail = rooms.map(room => {
        const surf = room.surface || 0;
        const roomType = ROOM_TYPES.find(rt => rt.id === room.type);
        const items = ROOM_ITEMS[room.type] || [];
        let subtotalRaw = 0;
        const emailLines = [];

        items.forEach(itemConfig => {
          const qty = room.quantities[itemConfig.key] || 0;
          if (qty === 0) return;
          const priceData = resolveItem(pricing, itemConfig.key);
          if (!priceData) return;

          const isSurfaceWork = !!(itemConfig.perSurface || itemConfig.sliderItem);
          const ep = effectivePrice(priceData, pos, isSurfaceWork);
          let lineTotal, detail;
          if (itemConfig.perSurface) {
            const area = surf * (itemConfig.surfaceMult || 1);
            lineTotal = area * ep;
            detail = `~${Math.round(area)} m²`;
          } else if (itemConfig.sliderItem && itemConfig.mlToM2) {
            const area = qty * WALL_HEIGHT;
            lineTotal = area * ep;
            detail = `${qty} ml → ${area.toFixed(1)} m²`;
          } else {
            lineTotal = qty * ep;
            detail = `${qty} ${priceData.unit}`;
          }
          subtotalRaw += lineTotal;
          emailLines.push({ label: itemConfig.label, detail, price: formatEur(Math.round(lineTotal)) });
        });

        if (room.customText?.trim()) {
          emailLines.push({ label: `Autre : ${room.customText.trim()}`, detail: 'à chiffrer', price: '—' });
        }

        return {
          name: multiFloor
            ? `${floorNameOf(room.floorId)} · ${roomType?.label || room.type}`
            : (roomType?.label || room.type),
          surface: surf,
          lines: emailLines,
          subtotal: formatEur(subtotalRaw),
        };
      });

      const res = await fetch('/api/send-devis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: {
            prenom: contact.prenom,
            nom: contact.nom,
            email: contact.email,
            telephone: contact.telephone,
            adresse: contact.adresse ? `${contact.adresse}, ${contact.ville}` : contact.ville,
            message: '',
          },
          rooms: roomsForEmail,
          total: formatEur(Math.round(estimate.ttc)),
          etat: state,
          etatLabel: stateMultiplier[state]?.label || state,
          tva: tvaKey,
          tvaLabel: TVA_TAUX[tvaKey]?.label || tvaKey,
          etages: multiFloor ? floors.map(f => f.name) : [],
          totalHT: formatEur(Math.round(estimate.htTotal)),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');
      setSubmitted(true);
    } catch (err) {
      setSendError(`Échec de l'envoi : ${err.message}. Écrivez-nous sur WhatsApp.`);
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-20">
        <div className="bg-white rounded-2xl shadow-xl p-12 max-w-lg w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-3">Estimation envoyée !</h2>
          <p className="text-slate-600 mb-2">Merci {contact.prenom} ! Votre estimation a été transmise à <strong>{contact.email}</strong>.</p>
          <p className="text-slate-500 text-sm mb-2">Un conseiller vous contactera sous <strong>24h</strong> pour affiner ce devis.</p>
          <div className="bg-orange-50 rounded-xl p-4 my-6 text-left">
            <p className="text-sm font-semibold text-orange-800 mb-1">Estimation indicative :</p>
            <p className="text-2xl font-extrabold text-orange-600">{formatEur(Math.round(estimate.ttc))} TTC</p>
            <p className="text-xs text-orange-600">({formatEur(Math.round(estimate.htTotal))} HT) — À affiner après visite</p>
          </div>
          <div className="space-y-3">
            <Link to="/" className="block w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-lg transition-colors">Retour à l'accueil</Link>
            <Link to="/realisations" className="block w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 rounded-lg transition-colors">Voir nos réalisations</Link>
          </div>
        </div>
      </div>
    );
  }

  // Carte d'une pièce — extraite de la boucle pour être rendue étage par étage.
  const renderRoomCard = (room) => {
                    const roomType = ROOM_TYPES.find(rt => rt.id === room.type);
                    const items = ROOM_ITEMS[room.type] || [];
                    const surf = room.surface || 0;
                    const surfMin = roomType?.surfaceMin || 2;
                    const surfMax = roomType?.surfaceMax || 60;
                    const isExpanded = expandedRoomId === room.id;

                    // Room subtotal for collapsed summary
                    const pos = stateMultiplier[state]?.pos ?? 0.33;
                    const roomSubtotal = items.reduce((sum, itemConfig) => {
                      const qty = room.quantities[itemConfig.key] || 0;
                      if (qty === 0) return sum;
                      const priceData = resolveItem(pricing, itemConfig.key);
                      if (!priceData) return sum;
                      const isSW = !!(itemConfig.perSurface || itemConfig.sliderItem);
                      const ep = effectivePrice(priceData, pos, isSW);
                      if (itemConfig.perSurface) return sum + surf * (itemConfig.surfaceMult || 1) * ep;
                      if (itemConfig.sliderItem && itemConfig.mlToM2) return sum + qty * WALL_HEIGHT * ep;
                      return sum + qty * ep;
                    }, 0);
                    const checkedCount = items.filter(i => (room.quantities[i.key] || 0) > 0).length;
                    const placoActive = PLACO_TRIGGER_KEYS.some(k => (room.quantities[k] || 0) > 0);

                    return (
                      <div key={room.id} id={`room-${room.id}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        {/* Room header — always visible, click to expand/collapse */}
                        <button
                          type="button"
                          onClick={() => setExpandedRoomId(isExpanded ? null : room.id)}
                          className="w-full text-left px-5 pt-4 pb-3 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{roomType?.icon}</span>
                              <div>
                                <h3 className="font-bold text-slate-900">{roomType?.label}</h3>
                                {!isExpanded && (
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    {surf} m²
                                    {checkedCount > 0 && ` · ${checkedCount} poste${checkedCount > 1 ? 's' : ''}`}
                                    {roomSubtotal > 0 && ` · ${formatEur(roomSubtotal)}`}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isExpanded && <span className="text-xl font-extrabold text-orange-600">{surf} m²</span>}
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); removeRoom(room.id); }}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                aria-label="Supprimer la pièce">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 6l1 13a2 2 0 002 2h8a2 2 0 002-2l1-13" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                                </svg>
                              </button>
                              <svg className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </button>

                        {/* Expandable content */}
                        {isExpanded && (
                          <>
                            {/* Surface slider */}
                            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                              <input
                                type="range" min={surfMin} max={surfMax} step="1"
                                value={surf}
                                onChange={e => updateRoomSurface(room.id, Number(e.target.value))}
                                className="w-full accent-orange-500 h-2"
                              />
                              <div className="flex justify-between text-xs text-slate-400 mt-1">
                                <span>{surfMin} m²</span><span>{surfMax} m²</span>
                              </div>
                            </div>

                            <div className="p-4 space-y-2">
                              {ITEM_GROUPS.filter(g => items.some(i => i.key.startsWith(g.id + '.'))).map(group => {
                                const gi = items.filter(i => i.key.startsWith(group.id + '.'));
                                const gSurface = gi.filter(i => i.perSurface);
                                const gSlider = gi.filter(i => i.sliderItem);
                                const gUnit = gi.filter(i => !i.perSurface && !i.sliderItem);
                                const activeCount = gi.filter(i => (room.quantities[i.key] || 0) > 0).length;
                                const isOpen = activeCount > 0 || (DEFAULT_OPEN_GROUPS[room.type] || []).includes(group.id);

                                return (
                                  <details key={group.id} ref={el => { if (el && !el.dataset.init) { el.open = isOpen; el.dataset.init = '1'; }}} className="group border border-slate-200 rounded-xl overflow-hidden">
                                    <summary className="flex items-center justify-between cursor-pointer px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors select-none list-none [&::-webkit-details-marker]:hidden">
                                      <div className="flex items-center gap-2">
                                        <span>{group.icon}</span>
                                        <span className="text-sm font-semibold text-slate-700">{group.label}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {activeCount > 0 && (
                                          <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">{activeCount}</span>
                                        )}
                                        <svg className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </div>
                                    </summary>
                                    <div className="p-3 space-y-2">
                                      {gSurface.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                          {gSurface.map(itemConfig => (
                                            <SurfaceItem key={itemConfig.key} pricing={pricing} itemConfig={itemConfig}
                                              surface={surf} checked={(room.quantities[itemConfig.key] || 0) > 0}
                                              onToggle={() => toggleSurfaceItem(room.id, itemConfig.key)} pos={pos}
                                              forced={itemConfig.key === 'murs.peinture_murs' && placoActive} />
                                          ))}
                                        </div>
                                      )}
                                      {gSlider.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                          {gSlider.map(itemConfig => (
                                            <SliderItem key={itemConfig.key} pricing={pricing} itemConfig={itemConfig}
                                              value={room.quantities[itemConfig.key] || 0}
                                              onChange={(qty) => updateSliderQty(room.id, itemConfig.key, qty)} pos={pos} />
                                          ))}
                                        </div>
                                      )}
                                      {gUnit.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                          {gUnit.map(itemConfig => (
                                            <UnitItem key={itemConfig.key} pricing={pricing} itemConfig={itemConfig}
                                              qty={room.quantities[itemConfig.key] || 0}
                                              onUpdate={(qty) => updateUnitQty(room.id, itemConfig.key, qty)} pos={pos} />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </details>
                                );
                              })}
                              <CustomItem text={room.customText || ''} onChange={(text) => updateCustomText(room.id, text)} />
                            </div>
                          </>
                        )}
                      </div>
                    );
  };

  return (
    <>
      <PageSEO
        title="Estimez votre projet — Configurateur Devis Gratuit"
        path="/devis"
        description="Estimez le coût de votre rénovation en 2 minutes. Configurateur de devis gratuit et instantané. Apex Rénovation intervient à Châteauroux et dans tout l'Indre (36) et le Berry."
      />
      <section className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm text-slate-400 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-orange-400">Accueil</Link><span>/</span>
            <span className="text-white">Configurateur devis</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-white mb-2">Estimez votre projet en 2 minutes</h1>
          <p className="text-slate-400">Entrez la superficie de chaque pièce — nous calculons automatiquement les surfaces de travaux</p>
        </div>
      </section>

      <section ref={stepSectionRef} className="py-10 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Steps */}
          <div className="mb-10">
            <div className="flex items-center">
              {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${i <= step ? 'bg-orange-600 border-orange-600 text-white' : 'bg-white border-slate-300 text-slate-400'}`}>
                      {i < step ? '✓' : i + 1}
                    </div>
                    <span className={`hidden sm:block text-sm font-medium whitespace-nowrap ${i === step ? 'text-slate-900' : 'text-slate-400'}`}>{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-3 ${i < step ? 'bg-orange-400' : 'bg-slate-200'}`} />}
                </React.Fragment>
              ))}
            </div>
            <p className="sm:hidden text-sm font-semibold text-slate-700 mt-3">
              Étape {step + 1} / {STEPS.length} — {STEPS[step]}
            </p>
          </div>

          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2 mb-8 lg:mb-0">

              {/* ── STEP 0 ── */}
              {step === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Votre bien</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-3">Type de bien</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[['maison','🏠','Maison'],['appartement','🏢','Appartement'],['local_commercial','🏪','Local commercial'],['bureau_pro','💼','Bureau']].map(([val, icon, label]) => (
                          <button key={val} onClick={() => setPropertyType(val)}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${propertyType === val ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-orange-300'}`}>
                            <span className="text-3xl block mb-1">{icon}</span>
                            <span className="text-xs font-semibold text-slate-700">{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Surface slider */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-slate-700">Surface totale du bien</label>
                        <span className="text-3xl font-extrabold text-orange-600">{totalSurface} m²</span>
                      </div>
                      <input
                        type="range" min="15" max="400" step="5"
                        value={totalSurface}
                        onChange={e => setTotalSurface(Number(e.target.value))}
                        className="w-full accent-orange-500 h-2"
                      />
                      <div className="flex justify-between text-xs text-slate-400 mt-1">
                        <span>15 m²</span><span>400 m²</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">État actuel</label>
                      <select value={state} onChange={e => setState(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 bg-white">
                        {Object.entries(stateMultiplier).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">TVA applicable</label>
                      <select value={tvaKey} onChange={e => setTvaKey(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 bg-white">
                        {Object.entries(TVA_TAUX).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>

                    <button onClick={() => goToStep(1)}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-lg transition-colors">
                      Configurer les pièces →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 1 ── */}
              {step === 1 && (
                <div className="space-y-5">
                  {floors.map((floor) => {
                    const floorRooms = rooms.filter(r => r.floorId === floor.id);
                    return (
                      <div key={floor.id} id={`floor-${floor.id}`} className="space-y-5">
                        {multiFloor && (
                          <div className="flex items-center gap-2 bg-slate-900 text-white rounded-xl px-4 py-2.5">
                            <span className="text-lg">🏠</span>
                            <input
                              value={floor.name}
                              onChange={e => renameFloor(floor.id, e.target.value)}
                              aria-label="Nom du niveau"
                              className="flex-1 min-w-0 bg-transparent font-bold text-white outline-none border-b border-transparent focus:border-orange-400"
                            />
                            <span className="shrink-0 text-xs text-slate-300">
                              {floorRooms.length} pièce{floorRooms.length > 1 ? 's' : ''}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFloor(floor.id)}
                              className="shrink-0 p-1 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/10 transition-colors"
                              aria-label="Supprimer le niveau">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M10 11v6M14 11v6M5 6l1 13a2 2 0 002 2h8a2 2 0 002-2l1-13M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                              </svg>
                            </button>
                          </div>
                        )}

                        <div className="bg-white rounded-2xl border border-slate-200 p-6">
                          <h2 className="text-xl font-bold text-slate-900 mb-1">
                            {multiFloor ? `Pièces — ${floor.name}` : 'Vos pièces'}
                          </h2>
                          <p className="text-sm text-slate-400 mb-4">Ajoutez chaque pièce à rénover, puis ajustez sa superficie — les surfaces de murs et plafond se calculent automatiquement.</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {ROOM_TYPES.map(rt => {
                              const count = floorRooms.filter(r => r.type === rt.id).length;
                              return (
                                <button key={rt.id} onClick={() => addRoom(rt.id, floor.id)}
                                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all text-sm font-medium text-slate-700 ${count > 0 ? 'border-orange-400 bg-orange-50' : 'border-slate-200 hover:border-orange-400 hover:bg-orange-50'}`}>
                                  <span>{rt.icon}</span>
                                  <span className="truncate flex-1 text-left">{rt.label}</span>
                                  {count > 0 && (
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                                      {count}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {floorRooms.length === 0 && (
                          <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
                            <p className="text-slate-400 text-lg">Ajoutez une pièce pour commencer</p>
                            <p className="text-slate-300 text-sm mt-1">Puis ajustez sa superficie — nous calculons le reste</p>
                          </div>
                        )}

                        {floorRooms.map(renderRoomCard)}
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={addFloor}
                    className="w-full border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 text-slate-600 hover:text-orange-700 font-semibold py-3 rounded-xl transition-colors">
                    + Ajouter un étage
                  </button>

                  {estimate.ht > 0 && (
                    <div className="lg:hidden bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center justify-between text-sm">
                      <span className="text-orange-700 font-medium">Estimation en cours</span>
                      <span className="text-orange-600 font-extrabold">{formatEur(Math.round(estimate.ttc))} TTC</span>
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => goToStep(0)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 rounded-lg transition-colors">← Retour</button>
                    <button onClick={() => goToStep(2)} disabled={rooms.length === 0 || estimate.ht === 0}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors">
                      Mes coordonnées →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2 ── */}
              {step === 2 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Vos coordonnées</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Prénom *</label>
                        <input required type="text" value={contact.prenom} onChange={e => updateContact('prenom', e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" placeholder="Jean" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Nom *</label>
                        <input required type="text" value={contact.nom} onChange={e => updateContact('nom', e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" placeholder="Dupont" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email * <span className="text-slate-400 text-xs">(l'estimation sera envoyée à cette adresse)</span></label>
                      <input required type="email" value={contact.email} onChange={e => updateContact('email', e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" placeholder="jean@email.fr" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Téléphone *</label>
                      <input required type="tel" value={contact.telephone} onChange={e => updateContact('telephone', e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" placeholder="06 XX XX XX XX" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Adresse du chantier</label>
                      <input type="text" value={contact.adresse} onChange={e => updateContact('adresse', e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" placeholder="12 rue des Acacias" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Ville</label>
                      <input type="text" value={contact.ville} onChange={e => updateContact('ville', e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" placeholder="Châteauroux" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button onClick={() => goToStep(1)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 rounded-lg transition-colors">← Retour</button>
                      <button onClick={() => goToStep(3)} disabled={!contact.prenom || !contact.email || !contact.telephone}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors">
                        Voir mon estimation →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── STEP 3 ── */}
              {step === 3 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Votre estimation détaillée</h2>
                  <p className="text-slate-500 text-sm mb-6">Barème indicatif — Devis définitif après visite technique gratuite</p>

                  {estimate.lines.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">Aucun poste sélectionné.</div>
                  ) : (
                    <div className="space-y-1 mb-6">
                      {estimate.lines.map((line, i) => (
                        <div key={i} className="flex items-start justify-between py-2 border-b border-slate-100 text-sm gap-3">
                          <div className="min-w-0">
                            <span className="text-orange-600 font-semibold">{line.roomLabel}</span>
                            <span className="text-slate-400 mx-1">·</span>
                            <span className="text-slate-700">{line.label}</span>
                            {!line.custom && <span className="block sm:inline text-slate-400 sm:ml-2 text-xs">({line.qty})</span>}
                          </div>
                          {line.custom
                            ? <span className="text-xs font-medium text-orange-500 shrink-0 italic">à chiffrer</span>
                            : <span className="font-semibold text-slate-900 shrink-0">{formatEur(Math.round(line.total))}</span>
                          }
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-slate-50 rounded-xl p-5 space-y-2 text-sm">
                    <div className="flex justify-between text-slate-600"><span>Sous-total travaux HT</span><span>{formatEur(Math.round(estimate.ht))}</span></div>
                    <div className="flex justify-between text-slate-600"><span>Suivi de travaux (5%)</span><span>{formatEur(Math.round(estimate.coordination))}</span></div>
                    <div className="flex justify-between font-semibold text-slate-800 border-t border-slate-200 pt-2"><span>Total HT</span><span>{formatEur(Math.round(estimate.htTotal))}</span></div>
                    <div className="flex justify-between text-slate-600"><span>TVA ({(estimate.tvaRate * 100).toFixed(1)}%)</span><span>{formatEur(Math.round(estimate.tva))}</span></div>
                    <div className="flex justify-between font-extrabold text-xl text-orange-600 border-t border-slate-300 pt-2">
                      <span>Total TTC</span><span>{formatEur(Math.round(estimate.ttc))}</span>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4 text-sm text-amber-800">
                    ⚠️ <strong>Estimation indicative non contractuelle.</strong> Les prix réels peuvent varier selon l'état du chantier et les matériaux choisis. Un devis définitif gratuit sera établi après visite technique.
                  </div>

                  {sendError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4 text-sm text-red-700">
                      {sendError}
                    </div>
                  )}

                  <div className="flex gap-3 mt-6">
                    <button onClick={() => goToStep(2)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 rounded-lg transition-colors">← Modifier</button>
                    <button
                      onClick={handleSendEmail}
                      disabled={sending}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {sending ? (
                        <><span className="animate-spin">⟳</span> Envoi en cours…</>
                      ) : (
                        'Recevoir par email ✓'
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-3">Envoyé depuis contact@apexrenovation.fr — 🔒 Données sécurisées</p>
                </div>
              )}
            </div>

            {/* ── Sidebar ── */}
            <div>
              <div className="sticky top-28 space-y-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">🧮</span> Estimation en cours
                  </h3>
                  {estimate.lines.length === 0 ? (
                    <p className="text-slate-400 text-sm">Ajoutez des pièces et sélectionnez des travaux pour voir votre estimation.</p>
                  ) : (
                    <>
                      <div className="space-y-1 mb-4 max-h-52 overflow-y-auto">
                        {estimate.lines.map((l, i) => (
                          <div key={i} className="flex justify-between text-xs text-slate-600 py-0.5">
                            <span className="truncate mr-2">{l.roomLabel} — {l.label}</span>
                            {l.custom
                              ? <span className="shrink-0 italic text-orange-400">à chiffrer</span>
                              : <span className="shrink-0 font-medium">{formatEur(Math.round(l.total))}</span>
                            }
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-slate-200 pt-3 space-y-1">
                        <div className="flex justify-between text-xs text-slate-500"><span>Travaux HT</span><span>{formatEur(Math.round(estimate.ht))}</span></div>
                        <div className="flex justify-between text-xs text-slate-500"><span>Suivi de travaux (5%)</span><span>{formatEur(Math.round(estimate.coordination))}</span></div>
                        <div className="flex justify-between text-sm text-slate-600 border-t border-slate-100 pt-1"><span>HT</span><span>{formatEur(Math.round(estimate.htTotal))}</span></div>
                        <div className="flex justify-between font-extrabold text-orange-600 text-lg mt-1">
                          <span>TTC</span><span>{formatEur(Math.round(estimate.ttc))}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
                  <p className="font-bold text-orange-800 mb-1 text-sm">Une question sur votre devis ?</p>
                  <p className="text-orange-700 text-xs mb-3">Notre assistant IA répond immédiatement.</p>
                  <Link to="/assistant" className="flex items-center justify-center gap-2 border border-orange-200 text-orange-600 font-bold py-2.5 rounded-lg hover:bg-orange-100 transition-colors text-sm">
                    ✦ Assistant IA
                  </Link>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5 text-xs text-slate-500 space-y-1.5">
                  <p className="font-semibold text-slate-700 text-sm mb-2">Ce devis inclut :</p>
                  {['Surfaces de murs et plafond calculées automatiquement', 'Tarifs adaptés à l\'état réel de votre bien', 'Suivi de travaux', 'TVA selon votre situation', 'Visite technique offerte après envoi'].map(t => (
                    <p key={t} className="flex gap-2"><span className="text-emerald-500">✓</span>{t}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
